/**
 * @file AnnouncementsElementPanel.tsx
 * @description Панель «Объявления» (phase19.16 Р2; решения пользователя
 * 2026-10-09/10): вид [Полоса][Карточки][Список] (для карточек — в ряд на
 * ПК и вид на телефоне, «Сменять каждые» — только у полосы); откуда —
 * [Из раздела «Акции»][Вписать вручную]: акции — сколько показывать и
 * текст (подробно / только название), ручные — до 5 строк с «куда ведёт»
 * и перетаскиванием; значок.
 * SB122 (uiux-settings-groups §3): подгруппы «Вид» (раскладка, значок) и
 * «Откуда объявления» — уровень 2 внутри группы «Содержимое».
 * SB120 этап 3б (uiux-sb120-stage3 §3 «Объявления»): у карточек — в ряд
 * 1–6 числом и на телефоне схемами (+ «по две»), смена полосы — числом
 * 2–30 с (↺ 6, `IntervalField`), быстрее 5 с — предупреждение.
 */

import { GRID_PER_ROW, SECTION_TEXT_LIMITS, SITE_LIMITS, defaultElementBody, type SectionElementOf } from "@chaiberi/shared";
import { Link } from "react-router";

import { cn } from "@/lib/utils";
import { JUMP_LINK_CLASS } from "../../../ui/jump-link";
import { Label } from "../../../ui/label";
import { SettingsSubgroup } from "../../../ui/settings-groups";
import { OptionCards } from "../../../site-view/OptionCards";
import { Segmented } from "../../../site-view/Segmented";
import { IconPicker } from "../fields/IconPicker";
import { SectionTargetField } from "../fields/SectionTargetField";
import { SectionTextField } from "../fields/SectionTextField";
import { SortableList } from "../fields/SortableList";
import { useSectionForm } from "../section-form-context";
import { useCurrentContext } from "../../../../auth/hooks";
import { GridCountField } from "./GridCountField";
import { IntervalField } from "./IntervalField";
import { PhoneGridField } from "./PhoneGridField";
import type { ElementPanelProps } from "./types";
import { nextItemId } from "../../../../lib/site-builder/section-edit";

type Announcements = SectionElementOf<"announcements">;
type Item = Announcements["items"][number];

/** Мини-схемы вида: полоса, карточки, список. */
function LayoutPreview({ layout }: { layout: Announcements["layout"] }) {
  if (layout === "strip") return <span className="h-2 w-full rounded-full bg-primary/60" />;
  if (layout === "cards") {
    return (
      <span className="flex w-full gap-1">
        {[0, 1, 2].map((i) => (
          <span key={i} className="h-6 flex-1 rounded-sm bg-primary/40" />
        ))}
      </span>
    );
  }
  return (
    <span className="flex w-full flex-col gap-1">
      {[0, 1, 2].map((i) => (
        <span key={i} className="h-1.5 w-full rounded-full bg-primary/40" />
      ))}
    </span>
  );
}

const DEFAULT_COLS = defaultElementBody("announcements").perRow;

const LAYOUTS = [
  { value: "strip" as const, label: "Полоса" },
  { value: "cards" as const, label: "Карточки" },
  { value: "list" as const, label: "Список" },
];

export function AnnouncementsElementPanel({ element, onChange, path }: ElementPanelProps<"announcements">) {
  const { hasIssuesUnder } = useSectionForm();
  const shop = useCurrentContext()?.shop?.slug ?? "";
  const set = (patch: Partial<Announcements>) => onChange({ ...element, ...patch });
  const count = Array.from({ length: SITE_LIMITS.announcements }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
  return (
    <>
      <SettingsSubgroup title="Вид">
        <OptionCards aria-label="Вид объявлений" columns={3} options={LAYOUTS.map((l) => ({ ...l, preview: <LayoutPreview layout={l.value} /> }))} value={element.layout} onChange={(layout) => set({ layout })} />
        {element.layout === "cards" ? (
          <>
            <GridCountField kind="announcements" field="perRow" values={GRID_PER_ROW} value={element.perRow} fallback={DEFAULT_COLS} path={path} onChange={(perRow) => set({ perRow: perRow ?? DEFAULT_COLS })} />
            <PhoneGridField ariaLabel="Карточки на телефоне" value={element.phoneLayout} onChange={(phoneLayout) => set({ phoneLayout })} />
          </>
        ) : null}
        {element.layout === "strip" ? <IntervalField value={element.intervalSec} path={path} onChange={(intervalSec) => set({ intervalSec })} /> : null}
        <IconPicker value={element.icon} onChange={(icon) => set({ icon })} />
      </SettingsSubgroup>
      <SettingsSubgroup title="Откуда объявления">
        <Segmented
          aria-label="Откуда объявления"
          options={[
            { value: "promotions" as const, label: "Из раздела «Акции»" },
            { value: "manual" as const, label: "Вписать вручную" },
          ]}
          value={element.source}
          onChange={(source) => set({ source, items: source === "manual" && element.items.length === 0 ? [{ id: "a1", text: "Объявление" }] : element.items })}
        />
        {element.source === "promotions" ? (
          <>
            <p className="type-caption text-on-surface-variant">Показываются действующие акции из раздела «Акции». Нет акций — блок на сайте скрыт.</p>
            <Link to={`/shop/${shop}/catalog/promotions`} className={cn(JUMP_LINK_CLASS, "self-start text-sm")}>
              Открыть «Акции» →
            </Link>
            <Label>Сколько показывать</Label>
            <Segmented aria-label="Сколько акций показывать" options={count} value={String(element.limit)} onChange={(v) => set({ limit: Number(v) })} />
            <Label>Текст</Label>
            <Segmented
              aria-label="Текст объявления об акции"
              options={[
                { value: "auto" as const, label: "Подробно: «Скидка 10 % на …»" },
                { value: "nameOnly" as const, label: "Только название акции" },
              ]}
              value={element.promoTextMode}
              onChange={(promoTextMode) => set({ promoTextMode })}
            />
          </>
        ) : (
          <SortableList<Item>
            label="Объявления"
            items={element.items}
            max={SITE_LIMITS.announcements}
            addLabel="Объявление"
            makeItem={() => ({ id: nextItemId(element.items, "a"), text: "" })}
            onChange={(items) => set({ items })}
            invalidAt={(i) => hasIssuesUnder(`${path}.items.${i}`)}
            summary={(item, i) => item.text || `Объявление ${i + 1}`}
            renderItem={(item, update, i) => (
              <>
                <SectionTextField label="Текст" path={`${path}.items.${i}.text`} value={item.text} max={SECTION_TEXT_LIMITS.announcement} onChange={(text) => update({ ...item, text })} />
                <SectionTargetField optional value={item.target ?? null} onChange={(target) => update({ ...item, target })} />
              </>
            )}
          />
        )}
      </SettingsSubgroup>
    </>
  );
}
